import React, { Component, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { formatTime } from 'utils/helper';
import { PLATFORM_ID } from 'utils/constants.js';
import * as platformUtils from 'utils/platform';
import style from './style.scss';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
import RestHelper from 'utils/RestHelper';
import { Button, Modal, Spin, notification, Tooltip, message, Space, Tag, Dropdown } from 'antd';
import { connect } from 'react-redux';
import { orange } from '@ant-design/colors';
import {
  selectCurrentAppCustomUserSceneTagLabel,
  selectHasSpecifiedPermissionInCurrentApp, selectNullableCurrentAppSettings
} from 'utils/selectors/selectors';
import {formatBytes} from "utils/format/file";
import {isNotNullish, isNullish} from "utils/nullish";
import {downloadAsFile} from "utils/file-utils";
import { countryFlagUrl, countryI18n } from 'utils/country';
import { CrashUtil } from 'utils/api/crash';
import { dataComplianceConfirm } from 'utils/dataComplianceConfirm';
import { isZh, ze } from 'utils/zhEn';
import JSZip from 'jszip';
import { MetricKitDetailListUtil } from 'components/exception/MetricKitPage/MetricKitDetailList';
import { VersionUtil } from 'utils/version-util';
import {
  isGameConsole,
  isMobile,
  PlatformUtil,
  isPlaystation,
  isSwitch,
  isWechatMiniProgram,
  isLinux,
  isPc,
  isAndroid,
  isIos,
  isHarmony,
  isAndroidOrHarmony,
} from 'utils/platform';
import { WebAppSettings } from 'utils/web-app-settings';
import memoize from 'memoize-one';
import { ClipboardUtil } from 'utils/clipboard-util';
import { UserIdDescriptionConfigUtil } from 'utils/user-id-description-config-util';
import { CS_STYLES } from 'utils/constants/style-constants';
import { getBackendInjectEnvs } from 'utils/backend-inject-envs';
import { Link } from 'react-router-dom';
import { ExceptionCategoryUtil } from 'utils/exception-category';
import { EnvUtil } from 'utils/env-util';
import { BillingTeamType } from 'utils/constants/billing';
import UnfoldUseridIcon from "svg/v2/newcs_dashboard_crashanalysis_issuelist_unfold_userid_icon_normal.svg";
import WrappedTipsIcon from 'components/antd-extension/WrappedTipsIcon.jsx';
import { BrandIconUtil } from 'utils/brand-icon-util';
import PinnedIcon from 'svg/v2/newcs_dashboard_crashanalysis_issuedetail_tracedata_operate_yes_icon.svg';
import CopyreportIcon from 'svg/v2/newcs_dashboard_crashanalysis_issuedetail_copyreport_icon_normal.svg';
import { EXCEPTION_TYPE_INT_ANDROID_NATIVE } from 'utils/constants/exception-type-int';
import { ServerAppSettings } from 'utils/server-app-settings';
import { VmTypeInt, VmTypeUtil } from 'utils/constants/vm-type';
import {
  MUMU_INCOMPLETE_STACK_HINT_EN,
  MUMU_INCOMPLETE_STACK_HINT_ZH,
  shouldShowMumuIncompleteStackHint,
} from 'utils/mumu-stack-hint';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { ix } from 'utils/i18n-with-context';
import { DocsUtil } from 'utils/docs-util';
import ReportScreenshot from 'components/exception/issue/ReportDetail/ReportScreenshot';
import ReportJankSampleFlameGraph from 'components/exception/issue/ReportDetail/ReportJankSampleFlameGraph';

// 信号处理状态的Tooltip内容
const SIGNAL_PROCESSING_STATUS_TOOLTIP = (
  <div>
    <p>{ ze('信号处理状态：', 'Signal Processing Status:') }</p>
    <p>{ ze('正常 - 信号处理正常，崩溃详情中显示的信息是完整的。', 'Normal - The signal is processed successfully, and the information displayed in the crash details is complete.') }</p>
    <p>{ ze('异常中断 - 信号处理的过程中，由于一些异常，处理过程被中断。下次App启动初始化时，CrashSight会整理已收集到的信息并上报，但崩溃详情中显示的信息可能不完整，可能会缺失自定义数据，系统日志， 部分或全部附件。', 'Interrupted - Signal processing was interrupted due to some exceptions. Once the app is launched again, CrashSight organizes the collected information and reports the crash, but the information displayed in the crash details may be incomplete. In this situation, custom data, system log and some or all attachments may be lost.') }</p>
  </div>
);

export const ReportDetailUtil = Object.freeze({
  renderExpAddr(expAddr) {
    if (!expAddr) {
      return '-';
    }
    const addrAsNumber = Number(expAddr);
    if (addrAsNumber >= 0x1000) {
      return expAddr;
    }
    // 空指针的提示不能保证一定准确，避免海外商业化项目疑问，只对国内项目开放
    return <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div>{ expAddr }</div>
      { EnvUtil.isChinaEnv() && <Tag color='processing'>{ ze('空指针', 'Null Pointer') }</Tag> }
    </div>;
  },

  /**
   * Hook: 获取 PerfSight 对局关联链接
   * @param {Object} params
   * @param {string} params.appId - 应用ID
   * @param {string} params.matchUuid - 对局UUID
   * @param {string} params.uploadTimeFormatted - 格式化的上传时间
   * @returns {string} url - PerfSight 对局链接
   */
  usePsMatchedReportLink({ appId, matchUuid, uploadTimeFormatted }) {
    const crashUploadTimeMillis = useMemo(() => {
      return !!uploadTimeFormatted ? moment(uploadTimeFormatted).valueOf() : null;
    }, [uploadTimeFormatted]);

    const [url, setUrl] = useState('');

    useEffect(() => {
      if (!matchUuid || !crashUploadTimeMillis) {
        return;
      }

      async function fetchPsRelatedLink() {
        const rsp = await RestHelper.mustPost('/redir/api/ps/getPerfsightOOMMatchByMatchUUid', {
          appId,
          matchUuid,
          crashUploadTimeMillis,
        });
        const url = rsp.json.data?.url;
        setUrl(url);
      }

      fetchPsRelatedLink();
    }, [appId, matchUuid, crashUploadTimeMillis]);

    return url;
  },

  /**
   * Hook: 检查用户是否命中安全策略
   * @param {Object} params
   * @param {string} params.appId - 应用ID
   * @param {string} params.userId - 用户ID
   * @param {string} params.uploadTimeFormatted - 格式化的上传时间
   * @returns {Object} { isHit, crc } - 是否命中安全策略及策略ID
   */
  useUserSecurityStrategy({ appId, userId, uploadTimeFormatted }) {
    const [result, setResult] = useState({ isHit: false, crc: null });

    useEffect(() => {
      if (!userId || !uploadTimeFormatted) {
        return;
      }
      // 海外环境不支持
      if (!EnvUtil.isChinaEnv()) {
        return;
      }

      async function checkUserStrategyHit() {
        const rsp = await RestHelper.mustPost('/redir/api/ps/checkUserStrategyHit', {
          appId,
          userId,
          time: uploadTimeFormatted,
        });
        const { isHit, crc } = rsp.json.data || {};
        setResult({ isHit, crc });
      }

      checkUserStrategyHit();
    }, [appId, userId, uploadTimeFormatted]);

    return result;
  },

  /**
   * 渲染国家或地区（用于表格展示）
   * @param {string} srcCountry - 国家代码
   * @returns {React.ReactNode}
   */
  renderCountryOrRegionForTable(srcCountry) {
    if (!srcCountry) {
      return '-';
    }
    const flagUrl = countryFlagUrl(srcCountry);
    return <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      { !!flagUrl && <img style={{ width: '20px' }} alt={srcCountry} src={flagUrl} /> }
      <div>{ countryI18n(srcCountry || '-') }</div>
    </div>;
  },

  /**
   * 计算CPU显示文本，与详情页renderCpu保持一致。
   * 安卓端有soc时，cpuName不拼入CPU文本（cpuName会在SoC字段单独显示）。
   */
  renderCpuText(crashMap, pid) {
    const { cpuType, cpuName, soc } = crashMap || {};
    if (isMobile(pid) && cpuType) {
      return (cpuName && !soc) ? `${cpuType} (${cpuName})` : cpuType;
    } else if (cpuName) {
      return cpuName;
    }
    return '-';
  },

  /**
   * 计算SoC显示文本，与详情页renderSoc保持一致。
   * soc字段由后端填充，仅安卓端有值。
   */
  renderSocText(crashMap) {
    const { soc, cpuName } = crashMap || {};
    if (!soc) {
      return '-';
    }
    return cpuName ? `${cpuName} (${soc})` : soc;
  },

  /**
   * 从 crashMap.reservedMap / reservedMapStr 读取 DFM 安全策略列表。
   * 数据来自 ES reservedMapStr.dfm_safe_strategies（JSON 数组字符串）。
   */
  getDfmSafeStrategies(crashMap) {
    const raw = crashMap?.reservedMap?.dfm_safe_strategies
      ?? (() => {
        const reservedMapStr = crashMap?.reservedMapStr;
        if (!reservedMapStr || typeof reservedMapStr !== 'string') {
          return null;
        }
        try {
          return JSON.parse(reservedMapStr)?.dfm_safe_strategies;
        } catch (e) {
          return null;
        }
      })();
    if (!raw) {
      return [];
    }
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  },

  /**
   * 将策略 event_time（秒）格式化为可读时间；无效则返回空串。
   */
  formatDfmSafeEventTime(eventTime) {
    const seconds = Number(eventTime);
    if (!Number.isFinite(seconds) || seconds <= 0) {
      return '';
    }
    return moment.unix(seconds).format('YYYY-MM-DD HH:mm:ss');
  },

  /**
   * 自定义信息 K#CSCRC 展示：在 CRC 旁拼接下发时间；多 CRC 换行，避免单行超框。
   * 例：
   * 1558（下发时间：2026-07-28 20:47:34）
   * 1559（下发时间：2026-07-28 10:00:00）
   */
  formatCscrcDisplayValue(crcValue, crashMap) {
    const strategies = ReportDetailUtil.getDfmSafeStrategies(crashMap);
    if (!strategies.length) {
      return crcValue;
    }

    const eventTimeLabel = ze('下发时间', 'Issued at');
    const crcList = String(crcValue || '')
      .split(',')
      .map(x => x.trim())
      .filter(Boolean);

    const formatOne = (crc, eventTime) => {
      const timeText = ReportDetailUtil.formatDfmSafeEventTime(eventTime);
      if (!timeText) {
        return String(crc);
      }
      return `${crc}（${eventTimeLabel}：${timeText}）`;
    };

    if (crcList.length > 0) {
      return crcList.map((crc) => {
        const matched = strategies.find(s => String(s?.crc) === crc);
        return formatOne(crc, matched?.event_time);
      }).join('\n');
    }

    return strategies
      .map(s => formatOne(s?.crc, s?.event_time))
      .filter(Boolean)
      .join('\n') || crcValue;
  },

  /**
   * 读取启动崩溃标记：优先 CrashMap.startupCrash（es-query setStartupCrash），
   * 回退 reservedMap.startupCrash（reservedMapStr 反序列化后一定有，不依赖 web-server 发版）。
   */
  getStartupCrashValue(crashMap) {
    const v = crashMap?.startupCrash ?? crashMap?.reservedMap?.startupCrash;
    if (v === true || v === 'true' || v === false || v === 'false') {
      return v;
    }
    return null;
  },

  /**
   * MetricKit 启动崩溃展示文本。
   */
  renderStartupCrashText(crashMap) {
    const v = ReportDetailUtil.getStartupCrashValue(crashMap);
    if (v === true || v === 'true') {
      return ze('是', 'Yes');
    }
    if (v === false || v === 'false') {
      return ze('否', 'No');
    }
    return '-';
  },

  /**
   * 读取启动卡死标记：优先 CrashMap.startupHang，回退 reservedMap.startupHang。
   * access：MetricKit Watchdog(143) 且全堆栈无 CrashSight 时打标。
   */
  getStartupHangValue(crashMap) {
    const v = crashMap?.startupHang ?? crashMap?.reservedMap?.startupHang;
    if (v === true || v === 'true' || v === false || v === 'false') {
      return v;
    }
    return null;
  },

  /** MetricKit 启动卡死展示文本。 */
  renderStartupHangText(crashMap) {
    const v = ReportDetailUtil.getStartupHangValue(crashMap);
    if (v === true || v === 'true') {
      return ze('是', 'Yes');
    }
    if (v === false || v === 'false') {
      return ze('否', 'No');
    }
    return '-';
  },

  /**
   * 渲染前/后台状态（用于表格展示）
   * @param {string} appInBack - 前后台状态值 ('true'/'false')
   * @param {Function} t - i18n翻译函数
   * @returns {string}
   */
  renderAppInBackForTable(appInBack, t) {
    if (appInBack === 'false') {
      return t('REPORTDETAIL.foreground');
    } else if (appInBack === 'true') {
      return t('REPORTDETAIL.background');
    }
    return t('REPORTDETAIL.unknown');
  },

  /**
   * 渲染运行时长（用于表格展示）
   * @param {number} elapsedTime - 运行时长（毫秒）
   * @returns {string}
   */
  renderElapsedTimeForTable(elapsedTime) {
    if (isNotNullish(elapsedTime) && elapsedTime >= 0) {
      return formatTime(elapsedTime);
    }
    return '-';
  },

  /**
   * 渲染ROM信息（用于表格展示）
   * @param {string} rom - ROM字符串
   * @returns {string}
   */
  renderRomForTable(rom) {
    if (!rom) {
      return '-';
    }
    const roms = rom.split('%2F');
    if (roms.length > 1) {
      return decodeURIComponent(`${roms[0]}/${roms[1]}`);
    }
    return decodeURIComponent(roms[0]);
  },

  /**
   * 渲染应用位数（用于表格展示）
   * @param {number} appBit - 应用位数
   * @returns {string}
   */
  renderAppBitForTable(appBit) {
    if (isNotNullish(appBit) && appBit > 0) {
      return `${appBit}${ze('位', ' Bit')}`;
    }
    return ze('未识别', 'Unknown');
  },

  /**
   * 渲染App运行环境/vmType（用于表格展示，简化版）
   * @param {number} vmType - vmType值
   * @returns {string}
   */
  renderVmTypeForTable(vmType) {
    if (!vmType) {
      return '-';
    }
    return VmTypeUtil.toI18n(vmType) || '-';
  },

  /**
   * 渲染GPU显卡名称（用于表格展示，PC端）
   * @param {string} brand - 显卡名称（crashMap.brand）
   * @returns {React.ReactNode}
   */
  renderGpuForTable(brand) {
    if (!brand) {
      return '-';
    }
    return BrandIconUtil.makePcCpuGpuLabelWithIcon(brand);
  },
});

export const UrlRenderer = ({ value }) => {
  if (!isNotNullish(value)) {
    return (<p>{ '-' }</p>);
  }
  const isHttpLink = typeof value ==='string' && (value.startsWith('http:') || value.startsWith('https:'));
  return isHttpLink? (
    <div
      style={{
        display: 'flex',
        gap: '15px',
        cursor: 'pointer',
        width: '100%',
      }}
    >
      <a
        href={value}
        target="_blank"
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: 'calc(100% - 24px)',
        }}
      >
        {value}
      </a>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
        }}
        onClick={() => {
          ClipboardUtil.copyToClipboard(value);
          message.success(ze('已复制至剪贴板', 'Copied to clipboard.'));
        }}
      >
        <CopyreportIcon />
      </div>
    </div>
  ) : (
    <p>{ value }</p>
  );
};

// 需求：--story=126903682 【CS】【NGR】CrashSight上报记录关联PerfSight对局
const PsMatchedReportLink = ({
  appId,
  matchUuid,
  uploadTimeFormatted,
}) => {
  const url = ReportDetailUtil.usePsMatchedReportLink({
    appId,
    matchUuid,
    uploadTimeFormatted,
  });

  if (!url) {
    return null;
  }
  return <li>
    <p className={style.label}>{ ze('PerfSight对局', 'PerfSight Report') }</p>
    <p className={style.detail}><UrlRenderer value={url}/></p>
  </li>
};

// 需求：--story=129973455 【CS】 【p0】【穿越火线】安全数据下发，标记安全相关崩溃
const UserSecurityStrategy = ({
  appId,
  userId,
  uploadTimeFormatted,
}) => {
  const { isHit, crc } = ReportDetailUtil.useUserSecurityStrategy({
    appId,
    userId,
    uploadTimeFormatted,
  });

  if (!isHit) {
    return null;
  }

  return <li>
    <p className={style.label} style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
      <div>{ ze('安全策略ID', 'Security Strategy ID') }</div>
      <Tooltip
        title={ze(
          '业务安全团队下发的安全策略ID，具体含义可以联系游戏具体安全团队接口人。',
          'Security strategy ID issued by the Tencent security team. For specific information, please contact your game security team representative.',
        )}
      ><WrappedTipsIcon /></Tooltip>
    </p>
    <p className={style.detail}>{ crc || '-' }</p>
  </li>
};

/**
 * ReportDetail：问题详情页里"上报详情"区域，负责展示单条上报（crash）的详细信息。
 *
 * 渲染的字段基本都来自 crashMap 和 esMap 两个 dict（均为父组件通过 props 传入的单条上报数据）：
 *   - esMap：该上报在 ES 里落地的原始数据，字段名/格式因平台而异，且包含较多定制化/私有字段。
 *   - crashMap：后端基于 esMap 加工后的字段集合（加工逻辑见 es-query-server 的
 *     MakeCrashMapService#makeOptionalCrashMap），作用是(1) 抹平不同平台在 esMap 里的字段差异
 *     (2) 作为对外（含 OpenAPI）公开的字段集合，esMap 里部分客户定制字段不会放进来。
 * 渲染优先级：优先使用 crashMap 里的字段；只有 crashMap 未提供（通常是定制化字段）时才直接读 esMap。
 * 不同平台（Android/iOS/PC/主机等）同一信息的字段名、展示逻辑可能不同，新增/修改字段展示时要注意区分平台分支。
 */
@connect((state) => {
  return {
    getHasSpecifiedPermission: (permission) => selectHasSpecifiedPermissionInCurrentApp(state, permission),
    appIdToWebAppSettings: state.app.get('appIdToWebAppSettings'),
    currentUser: state.user.get('current'),
    reduxState: state,
  };
}, (dispatch) => ({ dispatch }))
@withTranslation()
export default class ReportDetail extends Component {
  constructor(props) {
    super(props);
    this.state = {
      pcLogModalTitle: '',
      pcLogCosKey: '',
      pcLogLoading: false,
      pcLogText: '', // PC log转为文本的内容。如果log是二进制文件，转换后内容会失真，所以只能用于显示，不能用于下载
      pcLogUint8Array: null, // PC log的实际二进制内容，用于下载
      pcLogModalVisible: false,
    };
  }

  componentDidMount() {
  }

  getIsFromShareLink() {
    const { shareId } = this.props;
    return !!shareId;
  }

  getRom() {
    const { crashDoc } = this.props.issue.toJS();
    let roms = [];
    if (crashDoc?.crashMap?.rom) {
      roms = crashDoc?.crashMap?.rom.split('%2F');
    }
    let rom = this.props.t("REPORTDETAIL.romNull");
    if (roms.length > 0) {
      if (roms.length > 1) {
        rom = `${roms[0]}/${roms[1]}`;
      } else {
        rom = roms[0];
      }
    }
    return rom;
  }

  async openModalAndFetchPcCustomLog(logId) {
    const { appId, pid: platformId, t } = this.props;
    this.setState({ pcLogModalTitle: t("REPORTDETAIL.自定义日志来自文件") });
    const cosKey = `${logId}.log.inflate`;

    await dataComplianceConfirm();
    await this.openModalAndFetchPcCommonData(cosKey);

  }

  async openModalAndFetchPcCrashSightLog(logId) {
    const { appId, pid: platformId, t } = this.props;
    this.setState({ pcLogModalTitle: t("REPORTDETAIL.自定义日志来自接口") });
    const cosKey = `${logId}.crashSightLog.log.inflate`;

    await dataComplianceConfirm();
    await this.openModalAndFetchPcCommonData(cosKey);

  }

  async openModalAndFetchPcValueMap(logId) {
    const { appId, pid: platformId, t } = this.props;
    this.setState({ pcLogModalTitle: 'Value Map' });
    const cosKey = `${logId}.valueOtherMap.log.inflate`;

    await dataComplianceConfirm();
    await this.openModalAndFetchPcCommonData(cosKey);

  }

  async openModalAndFetchPcCommonData(cosKey) {
    const { appId, pid: platformId, t, shareId } = this.props;
    if (this.state.pcLogCosKey === cosKey) {
      this.setState({
        pcLogModalVisible: true,
      });
      return;
    }

    this.setState({
      pcLogLoading: true,
      pcLogText: '',
      pcLogModalVisible: true,
    });
    const rsp = await RestHelper.post('/api/crash/decompressInflateFile', {
      appId,
      platformId,
      shareId,
      cosKey,
    });
    const { hasFile, contentBase64 } = rsp.json.data;
    let pcLogText = t('REPORTDETAIL.日志不存在');
    let pcLogUint8Array = null;
    if (hasFile) {
      pcLogUint8Array = Uint8Array.from(window.atob(contentBase64), c => c.charCodeAt(0));
      pcLogText = new TextDecoder().decode(pcLogUint8Array);
    }
    this.setState({
      pcLogLoading: false,
      pcLogCosKey: cosKey,
      pcLogText,
      pcLogUint8Array,
    });
  }

  onClosePcLogModal() {
    this.setState({
      pcLogModalVisible: false,
    });
  }

  onDownloadPcLog() {
    const { pcLogModalTitle, pcLogUint8Array } = this.state;
    if (!pcLogUint8Array) {
      message.error('日志内容为空');
      return;
    }
    downloadAsFile(`${pcLogModalTitle}.txt`, pcLogUint8Array);
  }

  async onDownloadDump() {
    const {issue, appId, pid: platformId, t, shareId} = this.props;
    const { crashDoc } = issue.toJS() || {};
    const { esMap } = crashDoc || {};
    const cosKey = `${esMap.originMinidumpFileCoskey}.dmp.gz`
    const rsp = await RestHelper.post('/api/crash/decompressInflateFile',{
      appId,
      platformId,
      shareId,
      cosKey,
      compressFormat: 'GZIP',
    })
    const { hasFile, contentBase64 } = rsp.json.data;
    if (hasFile) {
      let pcLogUint8Array = Uint8Array.from(window.atob(contentBase64), c => c.charCodeAt(0));
      downloadAsFile(`${esMap.originMinidumpFileCoskey}.minidump.dmp.gz`, pcLogUint8Array)
    } else {
      message.error(t('ERRORSTACKTAB.Minidump内容为空'))
    }
  }

  decodeBase64(s, defaultOnFail = 'Decode BASE64 Failed') {
    if (!s) {
      return s;
    }
    try {
      return window.atob(s);
    } catch (e) {
      return defaultOnFail;
    }
  }

  parseUserIdDescriptionConfigCsvText = memoize((text) => UserIdDescriptionConfigUtil.parseCsvContent(text));

  hasViewPersonalDataPermission() {
    const { getHasSpecifiedPermission, shareLinkEquivalentUserPermissions } = this.props;
    return getHasSpecifiedPermission('VIEW_PERSONAL_DATA')
      || (this.getIsFromShareLink() && (shareLinkEquivalentUserPermissions || []).includes('VIEW_PERSONAL_DATA'));
  }

  renderUserIdFieldValue(userId) {
    const { appId, pid, appIdToWebAppSettings } = this.props;
    const settings = appIdToWebAppSettings[appId] || {};
    const userIdDescriptionConfigCsvText = settings[WebAppSettings.keys.userIdDescriptionConfigCsvText];
    const userIdDescriptionConfigInfo = this.parseUserIdDescriptionConfigCsvText(userIdDescriptionConfigCsvText);

    const descKeyValueList = userId
      ? userIdDescriptionConfigInfo.userIdToDescKeyValueList[userId] || []
      : [];

    const isValidUserId = String(userId).toLowerCase() !== 'unknown' && userId !== 'NO_PERMISSION';
    let userIdDiv;
    if (this.hasViewPersonalDataPermission() && userId && isValidUserId) {
      userIdDiv = <div>
        <Tooltip title={ze('查询此用户最近3天的上报记录', 'Query reports of this user in last 3 days.')} placement="top">
          <div style={{display:'inline-block', whiteSpace:'normal'}}>
            <Link
              to={`/crash-reporting/advanced-search/${appId}?${ new URLSearchParams({ pid, queryAccessPage: 1, queryAccessUserId: userId }).toString() }`}
              target='_blank'
              rel='noopener noreferrer'
            >
              <div>
                <span className={style.UnfoldUseridIcon}><UnfoldUseridIcon /></span>
                <span style={{ marginLeft: '4px', wordBreak: 'break-all' }}>{ userId }</span>
              </div>
            </Link>
          </div>
        </Tooltip>
      </div>;
    } else {
      userIdDiv = this.requirePermissionHoc( userId || 'Unknown');
    }

    return <div>
      <div>{ userIdDiv }</div>
      { descKeyValueList.map(({ key, value }, i) => {
        return <div key={i} style={{ fontSize: '14px', color: CS_STYLES.PRIMARY_COLOR }}>{ key }: { value }</div>;
      }) }
    </div>;
  }

  renderDefault(isAndroid) {
    const { isSmallSize } = this.props;
    const className = isSmallSize ? [style.report_detail, style.reportDetailSmallSize].join(' ') : style.report_detail;

    return (
      <div className={className}>
        <ul>
          <li>
            <p className={style.label}>{ this.props.t("REPORTDETAIL.异常进程") }</p>
            <p className={style.detail}>-</p>
          </li>
          <li>
            <p className={style.label}>{ this.props.t("REPORTDETAIL.异常线程") }</p>
            <p className={style.detail}>-</p>
          </li>
        </ul>
        <ul>
          <li>
            <p className={style.label}>{ this.props.t("REPORTDETAIL.userId") }</p>
            <p className={style.detail}>-</p>
          </li>
          <li>
            <p className={style.label}>{ this.props.t("REPORTDETAIL.occurTime") }</p>
            <p className={style.detail}>-</p>
          </li>
          <li>
            <p className={style.label}>{ this.props.t("REPORTDETAIL.postTime") }</p>
            <p className={style.detail}>-</p>
          </li>
          <li>
            <p className={style.label}>{isAndroid ? this.props.t("REPORTDETAIL.apkName") : this.props.t("REPORTDETAIL.bundleId")}</p>
            <p className={style.detail}>-</p>
          </li>
          <li>
            <p className={style.label}>{ this.props.t("REPORTDETAIL.version") }</p>
            <p className={style.detail}>-</p>
          </li>
          <li>
            <p className={style.label}>{ this.props.t("REPORTDETAIL.useTime") }</p>
            <p className={style.detail}>-</p>
          </li>
          <li>
              <p className={style.label}>{ this.props.t("REPORTDETAIL.feStatus") }</p>
              <p className={style.detail}>-</p>
            </li>
          <li>
            <p className={style.label}>{ this.props.t(isAndroid ? "oomFieldKeys.model" :"REPORTDETAIL.设备商品名") }</p>
            <p className={style.detail}>-</p>
          </li>
          <li>
            <p className={style.label}>{ this.props.t("REPORTDETAIL.osVersion") }</p>
            <p className={style.detail}>-</p>
          </li>
          {
            isAndroid && (
              <li>
                <p className={style.label}>{ this.props.t("REPORTDETAIL.rom") }</p>
                <p className={style.detail}>-</p>
              </li>
            )
          }
          <li>
            <p className={style.label}>{ this.props.t("REPORTDETAIL.cpu") }</p>
            <p className={style.detail}>-</p>
          </li>
        </ul>
      </div>
    );
  }

  getUserSceneTagLabel() {
    const { reduxState, t } = this.props;
    return selectCurrentAppCustomUserSceneTagLabel(reduxState) || t("issueCrashFilterKey.userSceneTag");
  }

  getIsShowChannelId() {
    const { reduxState, t } = this.props;
    const settings = selectNullableCurrentAppSettings(reduxState) || {};
    return !!settings[ServerAppSettings.keys.enableReportFieldChannelId];
  }

  getKeyToCustomKvConfig() {
    const { appId, reduxState } = this.props;
    const customKvConfigList = reduxState.app.get('appIdToCustomKvConfig')?.[appId] || [];
    const keyToCustomKvConfig = customKvConfigList.reduce((map, item) => {
      map[(item.key || '').replace(/^C03_/, '')] = item;
      return map;
    }, {});
    return keyToCustomKvConfig;
  }

  renderPcOrGameConsole() {
    const { appId, issue, pid, exceptionType, t, isSmallSize, appIdToWebAppSettings, style: extraStyle } = this.props;
    const settings = appIdToWebAppSettings[appId] || {};
    const { pcLogModalTitle, pcLogModalVisible, pcLogText, pcLogLoading } = this.state;
    let { crashDoc, exceptionName, pcHasWineModule } = issue.toJS();
    const isCrashArchived = !!crashDoc.archived;
    const crashMap = crashDoc.crashMap || {};
    const refinedCrashMap = CrashUtil.makeRefinedCrashMap(crashMap);
    const esMap = crashDoc.esMap || {};
    const isPcOrLinux = platformUtils.isPcOrLinux(pid);
    const isGameConsole = platformUtils.isGameConsole(pid);
    const isSwitch = platformUtils.isSwitch(pid);
    const isPlaystation = platformUtils.isPlaystation(pid);
    const isXbox = platformUtils.isXbox(pid);
    // 崩溃那边处理链暂不支持hasLogFile字段，所以按钮一直展示出来
    const isCrash = exceptionType === 'crashes' || exceptionType === 'crash';
    const showViewLogButton = isCrash || crashMap.hasLogFile;
    const showViewValueMapButton = esMap.hasValueMap;
    const showViewCrashSightLogButton = esMap.hasCrashSightLog;
    const pcLogTextLength = (pcLogText || '').length;
    const disableViewPcLogText = pcLogTextLength > 3 * 1024 * 1024; // 超过3M的pc log只能下载

    const quickViewCustomKeyList = WebAppSettings.getQuickViewCustomKeyList(settings);
    const attachCustomKvList = issue.get('attachCustomKvList') || [];
    const displayedCustomKvList = attachCustomKvList.filter(x => quickViewCustomKeyList.includes(x.keyWithType));

    const nintendoSwitchFields = {};
    if (isSwitch && esMap.thread) {
      let threadContent = {};
      try {
        threadContent = JSON.parse(esMap.thread);
      } catch (e) {
        notification.error({ message: 'crashMap.thread解JSON失败' });
      }
      nintendoSwitchFields.lrSymbol = threadContent.lrSymbol;
      nintendoSwitchFields.pcSymbol = threadContent.pcSymbol;
    }

    const {
      bucketPath,
      userId,
      crashTimeFormatted,
      uploadTimeFormatted,
      integratedOriginAppId,
      integratedOriginAppName,
      featureTagInfos,
    } = refinedCrashMap;

    const className = isSmallSize ? [style.report_detail, style.reportDetailSmallSize].join(' ') : style.report_detail;

    if (crashDoc && crashDoc.crashMap) {
      exceptionName = crashDoc.crashMap.expName || exceptionName;
    }

    // PS4/5特有字段
    const psFields = [
      'coreTargetClockGame',
      'coreTargetClockOs',
      'gfxEffectiveClock',
      'gfxTargetClock',
      'srcIp',
      'stopLocation',
      'stopReason',
      'titleId',
      'systemName',
      'vrrPegStatus',
      'vrrRange',
      // 'hardwareId',
      // 'stackTrace',
      // 'moduleCosUrl',
      // 'attachmentCosUrl',
    ];

    const psFieldsAlias = {
      'coreTargetClockGame': 'Core Target Clock Game',
      'coreTargetClockOs': 'Core Target Clock OS',
      'gfxEffectiveClock': 'GFX Effective Clock',
      'gfxTargetClock': 'GFX Target Clock',
      'srcIp': 'Src IP',
      'stopLocation': 'Stop Location',
      'stopReason': 'Stop Reason',
      'titleId': 'Title ID',
      'vrrPegStatus': 'VRR Peg Status',
      'vrrRange': 'VRR Range',
    }


    const psFilesLink = ((esMap.links || {}).files || {}).href;
    const psSelfLink = ((esMap.links || {}).self || {}).href;

    const {
      srcCountry,
      displayVersion,
      hardware,
      isNetbarMachine,
    } = esMap;

    return (
      <div className={className} style={extraStyle}>
        { isCrashArchived && this.renderArchivedCrashHint() }
        { this.renderDashboardFilterOutHint(esMap) }
        { this.renderSdkVersionHint(crashDoc.crashMap?.sdkVersion, pid) }
        { this.renderSecondReportHint(esMap) }
        { this.renderMetricKitHint(crashMap) }
        { this.renderAexitHint(crashMap) }
        { pcHasWineModule && this.renderPcHasWineModuleHint() }
        <ul>
          {/* <li>
            <p className={style.label}>{ this.props.t("REPORTDETAIL.异常进程") }</p>
            <p className={style.detail}>
              {crashDoc.crashMap.processName || "-"}
            </p>
          </li> */}
          {!isPlaystation && <li>
            <p className={style.label}>{ this.props.t("REPORTDETAIL.异常线程") }</p>
            <p className={style.detail}>
              {crashMap.threadName || '-'}
            </p>
          </li>}
          { (featureTagInfos || []).length > 0 && <li>
            <p className={style.label}>{ ze('特征标签', 'Feature Tag') }</p>
            <p className={style.detail}>{
              featureTagInfos.map((x, i) => <Tag key={i} color='blue'>{ x.name }</Tag>)
            }</p>
          </li> }
        </ul>
        <ul>
          { this.renderUserId(userId)  }
          { this.renderDeviceId(crashMap.deviceId) }
          <li>
            <p className={style.label}>{ this.props.t("REPORTDETAIL.occurTime") }</p>
            <p className={style.detail}>{ crashTimeFormatted }</p>
          </li>
          <li>
            <p className={style.label}>{ this.props.t("REPORTDETAIL.postTime") }</p>
            <p className={style.detail}>{ uploadTimeFormatted }</p>
          </li>
          { isPc(pid, appId) && <li>
            <p className={style.label}>{ this.props.t("REPORTDETAIL.mac") }</p>
            <p className={style.detail} title={crashDoc.crashMap.mac}>{crashDoc.crashMap.mac}</p>
          </li> }
          <li>
            <p className={style.label}>{ this.props.t("REPORTDETAIL.version") }</p>
            <p className={style.detail} title={crashDoc.crashMap.productVersion}>{crashDoc.crashMap.productVersion}</p>
          </li>
          { <li>
            <p className={style.label}>{ this.props.t("REPORTDETAIL.useTime") }</p>
            <p className={style.detail}>{ isNotNullish(crashMap.elapsedTime) ? formatTime(crashMap.elapsedTime) : '-' }</p>
          </li> }
          { isPcOrLinux && !isXbox && <li>
            <p className={style.label}>{ this.props.t('REPORTDETAIL.设备商品名') }</p>
            <p className={style.detail}>{ hardware || (platformUtils.isPc(pid, appId) ? 'Windows' : 'Linux') }</p>
          </li> }
          { !isXbox && <li>
            <p className={style.label}>{ this.props.t("REPORTDETAIL.osVersion") }</p>
            <p className={style.detail} title={crashDoc.crashMap.osVer}>{crashDoc.crashMap.osVer}</p>
          </li> }
          { isPc(pid,appId) && <li>
            <p className={style.label}>{ this.props.t("issueCrashFilterKey.cpu") }</p>
            <p className={style.detail} title={crashDoc.crashMap.cpuName}>
              { BrandIconUtil.makePcCpuGpuLabelWithIcon(crashDoc.crashMap.cpuName) }
            </p>
          </li>}
          { !EnvUtil.isChinaEnv() && this.renderCountryOrRegion(srcCountry) }
          { isPc(pid,appId) && <li>
            <p className={style.label}>{ t("REPORTDETAIL.freeMem")} / {t("REPORTDETAIL.memSize")} </p>
            <p className={style.detail}>
              {(crashMap.memSize >= 1 && crashMap.freeMem >= 1) ?
                formatBytes(crashMap.freeMem) + '/' + formatBytes(crashMap.memSize) : '-'}
            </p>
          </li>}
          { pcHasWineModule && <li>
            <p className={style.label}>{ t('issueCrashFilterKey.vmType') }</p>
            <p className={style.detail}>Wine</p>
          </li>}
          { isPc(pid,appId) && <li>
            { PlatformUtil.is(pid, PLATFORM_ID.PC)
              ? <p className={style.label} style={{ display: 'flex', alignItems: 'center', gap: '2px'}}>
                  <div>{ this.props.t("REPORTDETAIL.gpu") }</div>
                  <Tooltip
                    title={PlatformUtil.is(pid, PLATFORM_ID.PC)
                      ? ze('仅Windows Management Instrumentation列表中的首个GPU会被显示。', 'Only the first GPU in Windows Management Instrumentation list is displayed.')
                      : undefined}
                  ><WrappedTipsIcon /></Tooltip>
                </p>
              : <p className={style.label}>{ this.props.t("REPORTDETAIL.gpu") }</p>  }
            <p className={style.detail} title={crashDoc.crashMap.brand}>{ BrandIconUtil.makePcCpuGpuLabelWithIcon(crashDoc.crashMap.brand) }</p>
          </li>}
          { isPc(pid,appId) && <li>
            <p className={style.label}>{ t('issueCrashFilterKey.gpuDriverVersion') }</p>
            <p className={style.detail}>{ displayVersion || '-' }</p>
          </li> }
          {isPcOrLinux && !bucketPath && <li>
            <p className={style.label}>{ t("REPORTDETAIL.自定义日志来自文件") }</p>
            <p className={style.detail}>
              { showViewLogButton
                ? <Button onClick={() => this.openModalAndFetchPcCustomLog(crashDoc.crashMap.logId)}>{ t("PERMISSION.check") }</Button>
                : <div>-</div>
              }
            </p>
          </li>}
          {isPcOrLinux && !bucketPath && <li>
            <p className={style.label}>{ t("REPORTDETAIL.自定义日志来自接口") }</p>
            <p className={style.detail}>
              { showViewCrashSightLogButton
                ? <Button onClick={() => this.openModalAndFetchPcCrashSightLog(crashDoc.crashMap.logId)}>{ t("PERMISSION.check") }</Button>
                : <div>-</div>
              }
            </p>
          </li>}
          {isPcOrLinux && !bucketPath && <li>
            <p className={style.label}>Value Map</p>
            <p className={style.detail}>
              { showViewValueMapButton
                ? <Button onClick={() => this.openModalAndFetchPcValueMap(crashDoc.crashMap.logId)}>{ t("PERMISSION.check") }</Button>
                : <div>-</div>
              }
            </p>
          </li>}
          {<li>
            <p className={style.label}>{ t("issueCrashFilterKey.expUid") }</p>
            <p className={style.detail}>{ esMap.expUid || '-' }</p>
          </li>}
          {isSwitch && esMap.crashErrorCode && <li>
            <p className={style.label}>Error Code</p>
            <p className={style.detail} title={esMap.crashErrorCode}>{esMap.crashErrorCode}</p>
          </li>}
          {isSwitch && esMap.crashExceptionCode && <li>
            <p className={style.label}>Exception Code</p>
            <p className={style.detail} title={esMap.crashExceptionCode}>{esMap.crashExceptionCode}</p>
          </li>}
          {isSwitch && nintendoSwitchFields.lrSymbol && <li style={{ width: '50%' }}>
            <p className={style.label}>LR Symbol</p>
            <p className={style.detail} title={nintendoSwitchFields.lrSymbol}>{nintendoSwitchFields.lrSymbol}</p>
          </li>}
          {isSwitch && nintendoSwitchFields.pcSymbol && <li style={{ width: '50%' }}>
            <p className={style.label}>PC Symbol</p>
            <p className={style.detail} title={nintendoSwitchFields.pcSymbol}>{nintendoSwitchFields.pcSymbol}</p>
          </li>}
          {isSwitch && esMap.dyingMessageBase64 && <li style={{ width: '100%' }}>
            <p className={style.label}>Dying Message</p>
            <p className={style.detail}>{this.decodeBase64(esMap.dyingMessageBase64) || '-'}</p>
          </li>}
          {isPlaystation && psFields.map((x, i) => <li key={i}>
            <p className={style.label}>{ psFieldsAlias[x] ?? x }</p>
            <p className={style.detail}>{isNotNullish(esMap[x]) ? `${esMap[x]}` : '-'}</p>
          </li>)}
          {/* {isPlaystation && <li>
            <p className={style.label}>links.files</p>
            <p className={style.detail}><a href={psFilesLink} download>{ t("REPORTDETAIL.点击下载") }</a></p>
          </li>}
          {isPlaystation && <li>
            <p className={style.label}>links.self</p>
            <p className={style.detail}><a href={psSelfLink} download>{ t("REPORTDETAIL.点击下载") }</a></p>
          </li>} */}
          {isPlaystation && <li style={{ width: '100%' }}>
            <p className={style.label}>User Property</p>
            <p>{
              Object.entries(esMap.userProperty || {}).map(([k, v]) => {
                return <div style={{ fontSize: '14px' }}><Space style={{ marginBottom: '4px' }}>
                  <Tag>{k}</Tag>
                  <div>{v}</div>
                </Space></div>;
              })
            }</p>
          </li>}
          { !!esMap.userSceneTag && <li>
            <p className={style.label}>{ this.getUserSceneTagLabel() }</p>
            <p className={style.detail}>{ esMap.userSceneTag }</p>
          </li> }
          { integratedOriginAppId && <li>
            <p className={style.label}>{ t("REPORTDETAIL.原始转发项目") }</p>
            <p className={style.detail}>
              <div>{ integratedOriginAppName || '-' }</div>
              <div style={{ fontSize: '12px', color: CS_STYLES.PRIMARY_COLOR, lineHeight: 'normal' }}>App ID: { integratedOriginAppId }</div>
            </p>
          </li> }
          { this.getIsShowChannelId() && !!esMap.channelId && <li>
            <p className={style.label}>Channel ID</p>
            <p className={style.detail}>{ esMap.channelId }</p>
          </li>  }
          { isPc(pid, appId) && isNetbarMachine && <li> { /* 只展示是网吧机器的，不是网吧机器的不要展示 */ }
            <p className={style.label}>{ t('issueCrashFilterKey.isNetbarMachine') }</p>
            <p className={style.detail}>{ isNetbarMachine ? t('common.yes') : t('common.no') }</p>
          </li> }
          { this.renderCustomKvList(displayedCustomKvList, crashMap) }
          { !this.getIsFromShareLink() && <PsMatchedReportLink
            appId={appId}
            matchUuid={crashMap?.matchUuid}
            uploadTimeFormatted={uploadTimeFormatted}
          /> }
          { !this.getIsFromShareLink() && <UserSecurityStrategy
            appId={appId}
            userId={userId}
            uploadTimeFormatted={uploadTimeFormatted}
          /> }
          { this.renderReportScreenshot(appId, pid, crashMap) }
        </ul>
        { this.renderReportJankSampleFlameGraph() }
        <Modal
          title={pcLogModalTitle}
          visible={pcLogModalVisible}
          maskClosable={true}
          width='80%'
          onCancel={this.onClosePcLogModal.bind(this)}
          footer={[
            <Button type='primary' onClick={() => this.onDownloadPcLog()}>{t('REPORTDETAIL.点击下载')}</Button>,
            <Button onClick={this.onClosePcLogModal.bind(this)}>{t('common.close')}</Button>,
          ]}
        >
          <Spin spinning={pcLogLoading}>
            <pre>{disableViewPcLogText ? t('REPORTDETAIL.日志太大提示') : pcLogText}</pre>
          </Spin>
        </Modal>
      </div>
    )
  }

  requirePermissionHoc(node) {
    const { t } = this.props;
    if (this.hasViewPersonalDataPermission()) {
      return node;
    }
    return <div style={{ color: orange.primary }}>
      <Tooltip
        title={<div>
          <div>{t('common.当前用户角色缺少以下权限')}</div>
          <div>VIEW_PERSONAL_DATA</div>
        </div>}
      >{t('common.无权限')}<WrappedTipsIcon /></Tooltip></div>;
  }

  renderUserId(userId) {
    const { t, appId, pid } = this.props;
    const items = [{
      key: 'queryAccess',
      disabled: !userId || String(userId).toLowerCase() === 'unknown',
      label: <Link
        to={`/crash-reporting/advanced-search/${appId}?${ new URLSearchParams({ pid, queryAccessPage: 1, queryAccessUserId: userId }).toString() }`}
        target='_blank'
        rel='noopener noreferrer'
      >{ ze('查询此用户最近3天的上报记录', 'Query reports of this user in last 3 days') }</Link>,
    }];

    return <li>
      <p className={style.label} style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
        <div>{ t("REPORTDETAIL.userId") }</div>
        <Tooltip
          title={<div>
            {ze(
              '用于标识用户，可通过SetUserId接口设置，默认为“unknown”。',
              'Used to identify the user, can be set via the SetUserId interface, default is "unknown".',
            )}
            <a
              href={DocsUtil.makeDocsUrl('/terms/#CommonTerms')}
              target="_blank"
              rel="noopener noreferrer"
            >
              {ze('查看更多', 'View More')}
            </a>
          </div>}
        ><WrappedTipsIcon /></Tooltip>
      </p>
      <p className={style.detail}>{ this.requirePermissionHoc(this.renderUserIdFieldValue(userId)) }</p>
    </li>;
  }

  renderDeviceId(deviceId) {
    const { t, appId, pid } = this.props;

    const isValidDeviceId = String(deviceId).toLowerCase() !== 'unknown' && deviceId !== 'NO_PERMISSION';
    let deviceIdDiv;
    if (this.hasViewPersonalDataPermission() && deviceId && isValidDeviceId) {
      deviceIdDiv = <div>
        <Tooltip title={ix(ze('查询此设备最近3天的上报记录', 'Query reports of this device in last 3 days.'))} placement="top">
          <div style={{display:'inline-block', whiteSpace:'normal'}}>
            <Link
              to={`/crash-reporting/advanced-search/${appId}?${ new URLSearchParams({ pid, queryAccessPage: 1, queryAccessDeviceId: deviceId }).toString() }`}
              target='_blank'
              rel='noopener noreferrer'
            >
              <div>
                <span className={style.UnfoldUseridIcon}><UnfoldUseridIcon /></span>
                <span style={{ marginLeft: '4px', wordBreak: 'break-all' }}>{ deviceId }</span>
              </div>
            </Link>
          </div>
        </Tooltip>
      </div>;
    } else {
      deviceIdDiv = this.requirePermissionHoc( deviceId || 'Unknown');
    }

    return <li>
      <p className={style.label} style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
        <div>{ ix(t(("REPORTDETAIL.deviceId"))) }</div>
        <Tooltip
          title={<div>
            {ze(
              '用于标识设备，由SDK内部生成，在应用卸载前一直保持不变和有效。',
              'Used to identify devices, generated internally by the SDK, and remains constant and valid until the application is uninstalled.',
            )}
            <a
              href={DocsUtil.makeDocsUrl('/terms/#CommonTerms')}
              target="_blank"
              rel="noopener noreferrer"
            >
              {ze('查看更多', 'View More')}
            </a>
          </div>}
        ><WrappedTipsIcon /></Tooltip>
      </p>
      <p className={style.detail}>{ deviceIdDiv }</p>
    </li>
  }

  renderCustomKvList(displayedCustomKvList, crashMap) {
    const keyToCustomKvConfig = this.getKeyToCustomKvConfig();

    return displayedCustomKvList.map(x => {
      const keyWithType = x.keyWithType;
      const { alias } = keyToCustomKvConfig[keyWithType] || {};
      const keyDisplayText = alias ? `${alias} (${keyWithType})` : keyWithType;
      const displayValue = keyWithType === 'K#CSCRC'
        ? ReportDetailUtil.formatCscrcDisplayValue(x.value, crashMap)
        : x.value;

      return <li key={keyWithType}>
        <p className={style.label}>
          <span>{ keyDisplayText }</span>
          <span
            style={{ marginLeft: '2px', position: 'absolute', transform: 'translateY(-2px)' }}
          >
            <Tooltip
              title={ze('在“跟踪数据”页签下，设置了固定展示的自定义字段。', 'Pinned custom field from "Trace Data" tab.')}
            ><PinnedIcon/></Tooltip>
          </span>
        </p>
        <p className={style.detail}><UrlRenderer value={displayValue}/></p>
      </li>;
    });
  }

  renderCountryOrRegion(srcCountry) {
    const { t } = this.props;
    const flagUrl = countryFlagUrl(srcCountry);
    if (!srcCountry) {
      return null;
    }
    return <li>
      <p className={style.label}>{ t("issueCrashFilterKey.country") }</p>
      <p className={style.detail} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        { !!flagUrl && <img style={{ width: '20px' }} alt={srcCountry} src={flagUrl} /> }
        <div>{ countryI18n(srcCountry || '-') }</div>
      </p>
    </li>;
  }

  renderSoc(crashMap) {
    const { pid } = this.props;
    if (!isAndroid(pid) || !crashMap?.soc) {
      return null;
    }

    const value = ReportDetailUtil.renderSocText(crashMap);
    return <li>
      <p className={style.label}>SoC</p>
      <p className={style.detail}>{ value }</p>
    </li>;
  }

  /** MetricKit 单条详情：是否启动崩溃（全堆栈无 CrashSight 模块） */
  renderStartupCrash(crashMap) {
    if (!this.getIsMetricKit(crashMap) || ReportDetailUtil.getStartupCrashValue(crashMap) == null) {
      return null;
    }
    return <li>
      <p className={style.label}>{ ze('是否启动崩溃', 'Startup Crash') }</p>
      <p className={style.detail}>{ ReportDetailUtil.renderStartupCrashText(crashMap) }</p>
    </li>;
  }

  /** MetricKit 卡死分析：是否是启动卡死（Watchdog + 全堆栈无 CrashSight） */
  renderStartupHang(crashMap) {
    if (!this.getIsMetricKit(crashMap) || ReportDetailUtil.getStartupHangValue(crashMap) == null) {
      return null;
    }
    return <li>
      <p className={style.label}>{ ze('是否是启动卡死', 'Startup Hang') }</p>
      <p className={style.detail}>{ ReportDetailUtil.renderStartupHangText(crashMap) }</p>
    </li>;
  }

  renderCpu(crashMap) {
    const { pid } = this.props;
    const { cpuType, cpuName } = crashMap || {};

    let label = '';
    if (isMobile(pid) && cpuType) {
      label = this.props.t('REPORTDETAIL.cpu');
    } else if (cpuName) {
      label = this.props.t('issueCrashFilterKey.cpu');
    } else {
      return null;
    }

    const text = ReportDetailUtil.renderCpuText(crashMap, pid);
    return <li>
      <p className={style.label}>{ label }</p>
      <p className={style.detail}>{ text }</p>
    </li>;
  }

  renderMemory(crashMap, esMap) {
    const { t, pid } = this.props;
    const {
      freeProcessMem,
      totalProcessMem,
    } = esMap || {};

    if (isAndroidOrHarmony(pid) || isPc(pid) || PlatformUtil.isMac(pid)) {
      return <li>
        <p className={style.label}>{ t("REPORTDETAIL.freeMem")} / {t("REPORTDETAIL.memSize")} </p>
        <p className={style.detail}>
          {(crashMap.memSize >= 1 && crashMap.freeMem >= 1) ?
            formatBytes(crashMap.freeMem) + ' / ' + formatBytes(crashMap.memSize) : '-'}
        </p>
      </li>;
    } else if (isIos(pid)) {
      return <li>
        <p className={style.label}>{ t('TRACKDATATAB.processMemoryAvailable') }</p>
        <p className={style.detail}>
          {(freeProcessMem > 0 && totalProcessMem > 0) ?
            formatBytes(freeProcessMem) + ' / ' + formatBytes(totalProcessMem) : '-'}
        </p>
      </li>;
    } else {
      return null;
    }
  }

  renderVmType(crashMap, esMap) {
    const { t, currentUser } = this.props;
    const isSuperAdmin = !!currentUser.get('isSuper');
    const {
      rom,
    } = crashMap || {};
    const {
      vmType,
      vmDeviceBrand,
      reservedMapStr,
    } = esMap || {};

    let cloudGamingInstance;
    let cloudGamingDeviceId;
    if (vmType === VmTypeInt.CLOUD_GAMING && reservedMapStr) {
      try {
        const reservedMap = JSON.parse(reservedMapStr);
        cloudGamingInstance = reservedMap['E9'];
        cloudGamingDeviceId = reservedMap['E10'];
      } catch (e) {
        message.error('Parse reservedMapStr error.');
        console.error('Parse reservedMapStr error.', e);
      }
    }

    let vmTypeLabel = vmType && VmTypeUtil.toI18n(vmType);
    const lowerRom = (rom || '').toLowerCase();
    if (lowerRom.includes('tencent') && lowerRom.includes('gourd_arm64')) {
      vmTypeLabel = ze('云游戏', 'Cloud Gaming');
    }

    return vmType && (<li>
      <p className={style.label}>{ t('issueCrashFilterKey.vmType') }</p>
      <p className={style.detail}>
        <span>{ vmTypeLabel }</span>
        { vmType === VmTypeInt.EMULATOR && vmDeviceBrand && <span style={{ marginLeft: '4px' }}>({ vmDeviceBrand })</span> }
        { shouldShowMumuIncompleteStackHint(vmType, vmDeviceBrand, crashMap && crashMap.cpuType) && (
          <span style={{ marginLeft: '4px', color: CS_STYLES.PRIMARY_COLOR_ORANGE }}>
            { ze(MUMU_INCOMPLETE_STACK_HINT_ZH, MUMU_INCOMPLETE_STACK_HINT_EN) }
          </span>
        ) }
        { isSuperAdmin && vmType === VmTypeInt.EMULATOR && !vmDeviceBrand && <span style={{ marginLeft: '4px' }}>(code={ crashMap.isVirtualMachine })</span> }
        { cloudGamingInstance && <div style={{ marginTop: '4px' }}><Tag color='geekblue'>Instance</Tag>{cloudGamingInstance}</div> }
        { cloudGamingDeviceId && <div style={{ marginTop: '4px' }}><Tag color='geekblue'>Device ID</Tag>{cloudGamingDeviceId}</div> }
      </p>
    </li>)
  }

  renderCloudGamingFields(crashMap, esMap) {
    const {
      vmType,
    } = esMap;
    const {
      reservedMap,
    } = crashMap || {};

    if (vmType !== VmTypeInt.CLOUD_GAMING || !reservedMap) {
      return null;
    }

    const fields = [
      { key: 'vendor.gamematrix.hardware_type', label: ze('（云游戏）平台类型', '(Cloud Gaming) Platform Type') },
      { key: 'vendor.gamematrix.cluster', label: ze('（云游戏）平台集群', '(Cloud Gaming) Platform Cluster') },
      { key: 'javaprop.gamematrix.gameid', label: ze('（云游戏）Game ID', '(Cloud Gaming) Game ID') },
      { key: 'ro.tenc.cloudgame.gameid', label: ze('（云游戏）游戏集群', '(Cloud Gaming) Game Cluster') },
    ];

    return fields
      .filter(x => !!reservedMap[x.key])
      .map(x => {
        return <li key={x.key}>
          <p className={style.label}>{ x.label }</p>
          <p className={style.detail}>{ reservedMap[x.key] }</p>
        </li>;
      });
  }

  renderDashboardFilterOutHint(esMap) {
    const {
      dashboardFilterOut,
      crashDataType,
    } = esMap || {};

    let text = '';
    if (dashboardFilterOut) {
      text = ze('此上报命中了自定义大盘过滤策略。异常概览统计数据、自定义告警将忽略此上报。', 'This report matched the dashboard filters, will be excluded from overview statistics and alerts.');
    } else if (String(crashDataType) === 'true') {
      text = ze('此上报匹配到了系统退出关键字。异常概览统计数据、自定义告警将忽略此上报。', 'This report matched the system exit keywords, will be excluded from overview statistics and alerts.');
    }

    if (text) {
      return <div style={{ margin: '20px 0', width: '100%', color: CS_STYLES.PRIMARY_COLOR_ORANGE, fontSize: '12px' }}>
        <Space>
          <ExclamationCircleOutlined/>
          <div>{ text }</div>
        </Space>
      </div>
    } else {
      return null;
    }
  }

  renderArchivedCrashHint() {
    const text = ze('此上报数据已超过默认存储周期，已转移至归档存储。归档存储的上报无法通过高级搜索功能检索到。', 'This report data has exceeded the default storage period and has been moved to archived storage. It cannot be retrieved through the search function.');

    return <div style={{ margin: '20px 0', width: '100%', color: CS_STYLES.PRIMARY_COLOR_ORANGE, fontSize: '12px' }}>
      <Space>
        <ExclamationCircleOutlined/>
        <div>{ text }</div>
      </Space>
    </div>
  }

  getIsInTencentLocalNetwork() {
    // 判断是否为腾讯内网
    const isInTencentLocalNetwork = this.props.reduxState.user.get('isInTencentLocalNetwork')
    return isInTencentLocalNetwork
  }

  /** 判断是否是iOS MetricKit上报 */
  getIsMetricKit(crashMap) {
    const metrickit = crashMap?.reservedMap?.metrickit;
    // 判断 metrickit 是否为 true（boolean 或 string "true"）
    return metrickit === true || (typeof metrickit === 'string' && metrickit === 'true');
  }

  getIsAexit(crashMap) {
    const aexit = crashMap?.reservedMap?.aexit;
    return aexit === true || (typeof aexit === 'string' && aexit === 'true');
  }

  renderSdkVersionHint(version, pid) {
    // 数据未加载好时version为falsy，此时不应提示，避免误导用户
    if (!version) {
      return null;
    }
    const isPcOrLinux = platformUtils.isPcOrLinux(pid);
    // 判断GCloud的下载链接
    const getGcloudLink = () => {
      if (isPcOrLinux) {
        return isZh()
          ? 'https://gcloud.woa.com/sdk/pcDownloadLocal'
          : 'https://gcloud.woa.com/sdk/pcDownloadOverseas';
      } else {
        return isZh()
          ? 'https://gcloud.woa.com/sdk/download'
          : 'https://gcloud.woa.com/sdk/downloadOverseas';
      }
    };
    // 判断是否需要升级
    let isUpdate = false;
    // 移动端(android、ios、鸿蒙)低于4.3.0提示升级
    if(isMobile(pid) && VersionUtil.compareCsSdkVersion(version, '4.3.0') < 0) {
      isUpdate = true;
    }
    // pc端和linux低于2.2.3及以下提示升级
    if(isPcOrLinux && VersionUtil.compareCsSdkVersion(version, '2.2.3') <= 0) {
      isUpdate = true;
    }
    if (isUpdate) {
      return <div style={{ margin: '20px 0', width: '100%', color: CS_STYLES.PRIMARY_COLOR_ORANGE, fontSize: '12px' }}>
        <Space>
          <ExclamationCircleOutlined />
          <div>
          <span>{ze('当前SDK的版本过低，可能存在未修复的SDK问题，', 'The current SDK version is too low, there may be unresolved SDK issues,')}</span>
          <span>{ze('推荐更新到', 'recommended to update to the latest version on the')}</span>
            <a href={DocsUtil.makeDocsUrl('/changelog/#sdkchangelogs')} target="_blank">{ze('官网', '\nofficial website')}</a>
            {/* 区分是否为腾讯内网，不是内网则隐藏 */}
            {this.getIsInTencentLocalNetwork() ? <>
            <span>{ze('或', '\nor')}</span>
            <a href={getGcloudLink()} target="_blank">{ze('GCloud', '\nGCloud')}</a>
            </> : null}
            <span>{ze('最新版本。', '.')}</span>
          </div>
        </Space>
      </div>
    } else {
      return null
    }
  }

  renderSecondReportHint(esMap) {
    if (!esMap.isSecondReport) {
      return null;
    }

    return <div style={{ margin: '20px 0', width: '100%', color: CS_STYLES.PRIMARY_COLOR_ORANGE, fontSize: '12px' }}>
      <Space>
        <ExclamationCircleOutlined/>
        <div>
          { ze('信号处理的过程中发生异常中断，上报内容可能缺失部分信息。', 'Signal processing was interrupted due to some exceptions. The reported content may be missing some information.') }
        </div>
        <Tooltip title={SIGNAL_PROCESSING_STATUS_TOOLTIP}>
          <WrappedTipsIcon />
        </Tooltip>
      </Space>
    </div>
  }

  renderMetricKitHint(crashMap) {
    if (!this.getIsMetricKit(crashMap)) {
      return null;
    }

    return <div style={{ margin: '20px 0', width: '100%', color: CS_STYLES.PRIMARY_COLOR_ORANGE, fontSize: '12px' }}>
      <Space>
        <ExclamationCircleOutlined/>
        <div>
          <span>{ze('数据来源为', 'Data source is ')}</span>
          <a href="https://developer.apple.com/documentation/MetricKit" target="_blank">MetricKit</a>
          <span>{ze('。', '. ')}</span>
          <a style={{ color: CS_STYLES.PRIMARY_COLOR }} onClick={() => this.handleDownloadMetricKitFromCrash(crashMap)}>
            {ze('下载 MetricKit 原始数据', 'Download Raw MetricKit Data')}
          </a>
        </div>
      </Space>
    </div>
  }

  renderAexitHint(crashMap) {
    if (!this.getIsAexit(crashMap)) {
      return null;
    }

    return <div style={{ margin: '20px 0', width: '100%', color: CS_STYLES.PRIMARY_COLOR_ORANGE, fontSize: '12px' }}>
      <Space>
        <ExclamationCircleOutlined/>
        <div>
          <span>{ze('数据来源为', 'Data source is ')}</span>
          <a href="https://developer.android.com/reference/android/app/ApplicationExitInfo" target="_blank">ApplicationExitInfo</a>
          <span>{ze('。', '. ')}</span>
          <a style={{ color: CS_STYLES.PRIMARY_COLOR }} onClick={() => this.handleDownloadAexitFromCrash(crashMap)}>
            {ze('下载应用退出原始数据', 'Download Raw Application Exit Data')}
          </a>
        </div>
      </Space>
    </div>
  }

  handleDownloadAexitFromCrash(crashMap) {
    const attachList = this.props.issue?.toJS?.().attachList || [];
    const att = attachList.find(x => x.fileName === 'aexit.zip');
    if (!att || !att.content) {
      message.error(ze('未找到应用退出原始附件', 'Application exit attachment not found'));
      return;
    }
    // 附件在 attFileMap 里 gzip 存储；详情 API 经 CrashAllDoc 已 ungzip 后以 base64 下发，不是 PK zip 包
    const crashKey = crashMap?.crashId || crashMap?.expUid || 'raw';
    const name = `aexit_${crashKey}.json`;
    let downloadContent = att.content;
    try {
      downloadContent = Uint8Array.from(window.atob(att.content), c => c.charCodeAt(0));
    } catch (e) {
      // 旧路径或未 base64 时按原文下
    }
    downloadAsFile(name, downloadContent);
    message.success(ze('已开始下载', 'Download started'));
  }

  /**
   * 从上报详情页反向查找对应的原始 MetricKit 记录并下载。
   *
   * 查询条件：crashTime（精确范围）+ deviceId，不带 exceptionCategory（MetricKit 原始记录无此概念）。
   * - 0 条：报错
   * - 1 条：直接下载（委托 MetricKitDetailListUtil.downloadRawDataForCrashId）
   * - 多条（异常兜底）：warn 提示 + 打包 zip 下载
   */
  async handleDownloadMetricKitFromCrash(crashMap) {
    const { appId, pid: platformId } = this.props;
    const { crashTimestamp, deviceId, crashId } = crashMap;
    if (!crashTimestamp || !deviceId) {
      message.error(ze(
        `关联原始数据所需字段缺失：${[!crashTimestamp && '发生时间', !deviceId && '设备 ID'].filter(Boolean).join('、')}`,
        `Required field(s) missing: ${[!crashTimestamp && 'Crash Time', !deviceId && 'Device ID'].filter(Boolean).join(', ')}`,
      ));
      return;
    }
    const hideLoading = message.loading(ze('正在查询原始 MetricKit 数据...', 'Searching for raw MetricKit data...'), 0);
    try {
      const rsp = await RestHelper.mustPost('/api/crash/queryMetricKitReports', {
        appId,
        platformId,
        offset: 0,
        limit: 10,
        sortField: 'uploadTime',
        desc: true,
        search: {
          searchConditionGroup: {
            conditions: [
              { field: 'crashTime', queryType: 'RANGE', gte: crashTimestamp, lte: crashTimestamp },
              { field: 'deviceId', queryType: 'TERM', term: deviceId },
            ],
          },
        },
      });
      const records = rsp.json?.data?.items || [];
      if (records.length === 0) {
        message.error(ze('未找到关联的原始 MetricKit 数据', 'No related raw MetricKit data found'));
        return;
      }
      if (records.length === 1) {
        await MetricKitDetailListUtil.downloadRawDataForCrashId({ appId, crashId: records[0].crashId });
      } else {
        message.warning(ze(`关联到 ${records.length} 条原始数据，将打包下载`, `Found ${records.length} related records, downloading as zip`));
        const zip = new JSZip();
        for (const record of records) {
          const rspDetail = await RestHelper.mustPost('/api/crash/queryMetricKitDetail', {
            appId,
            crashIdList: [record.crashId],
          });
          const data = rspDetail.json?.data || {};
          const item = (data.items || [])[0] || null;
          const content = MetricKitDetailListUtil.formatContent(item ? item.metricKit : null);
          if (content) {
            zip.file(`MetricKit_${record.crashId}.txt`, content);
          }
        }
        const blob = await zip.generateAsync({ type: 'blob' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `MetricKit_${crashId || 'bundle'}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        message.success(ze('已开始下载', 'Download started'));
      }
    } catch (e) {
      message.error(ze('查询原始 MetricKit 数据失败', 'Failed to query raw MetricKit data'));
    } finally {
      hideLoading();
    }
  }

  renderPcHasWineModuleHint() {
    return <div style={{ margin: '20px 0', width: '100%', color: CS_STYLES.PRIMARY_COLOR_ORANGE, fontSize: '12px' }}>
      <Space>
        <ExclamationCircleOutlined/>
        <div>
          { ze(
            '在Wine环境下运行的程序，采集到的系统版本、显卡、显卡驱动版本等属性并非真实值。',
            'For programs running in a Wine environment, the collected system version, GPU, GPU driver version, and other attributes may not reflect the actual values.'
          ) }
        </div>
      </Space>
    </div>;
  }

  renderReportScreenshot(appId, pid, crashMap) {
    const { hasSdkScreenshot } = this.props;
    if (!hasSdkScreenshot) {
      return null;
    }
    return <ReportScreenshot
      appId={appId}
      pid={pid}
      crashMap={crashMap}
    />
  }

  renderReportJankSampleFlameGraph() {
    const { issue, isSmallSize, appId, pid } = this.props;
    // 预览小卡不展示：火焰图至少 300px，会把预览撑开
    if (isSmallSize) {
      return null;
    }
    return <ReportJankSampleFlameGraph issue={issue} appId={appId} pid={pid} />;
  }

  render() {
    const { appId, issue, pid, t, isSmallSize, exceptionType, appIdToWebAppSettings, currentUser, reduxState, style: extraStyle } = this.props;
    const teamType = reduxState.app.get('current').get('teamType');
    const isNotCommercialApp = teamType && teamType !== BillingTeamType.COMMERCIAL; // 是否非商业化项目

    const isSuperAdmin = !!currentUser.get('isSuper');
    const settings = appIdToWebAppSettings[appId] || {};
    const quickViewCustomKeyList = WebAppSettings.getQuickViewCustomKeyList(settings);
    const userIdDescriptionConfigCsvText = settings[WebAppSettings.keys.userIdDescriptionConfigCsvText];
    const userIdDescriptionConfigInfo = this.parseUserIdDescriptionConfigCsvText(userIdDescriptionConfigCsvText);

    let { crashDoc, exceptionName, issueExceptionType: issueExceptionTypeInt } = issue.toJS();
    const attachCustomKvList = issue.get('attachCustomKvList') || [];
    const isAndroid = parseInt(pid) === PLATFORM_ID.ANDROID;
    const isIos = parseInt(pid) === PLATFORM_ID.IOS;

    const displayedCustomKvList = attachCustomKvList.filter(x => quickViewCustomKeyList.includes(x.keyWithType));

    if (!crashDoc) {
      return this.renderDefault(isAndroid);
    }
    if (platformUtils.isPcOrLinux(pid)) {
      return this.renderPcOrGameConsole();
    }
    if (platformUtils.isGameConsole(pid) && !ExceptionCategoryUtil.isError(exceptionType)) {
      return this.renderPcOrGameConsole();
    }

    const isCrashArchived = !!crashDoc.archived;
    const crashMap = crashDoc.crashMap || {};
    const refinedCrashMap = CrashUtil.makeRefinedCrashMap(crashMap);
    const {
      userId,
      deviceId,
      crashTimeFormatted,
      uploadTimeFormatted,
      integratedOriginAppId,
      integratedOriginAppName,
      appBit,
      featureTagInfos,
    } = refinedCrashMap;

    const esMap = crashDoc.esMap || {};

    const { srcCountry, hasMinidump, vmType } = esMap;

    const rom = this.getRom();

    const isMetricKit = this.getIsMetricKit(crashMap);

    let appInBack = this.props.t("REPORTDETAIL.unknown");
    if (crashMap.appInBack === 'false') {
      appInBack = this.props.t("REPORTDETAIL.foreground");
    } else if (crashMap.appInBack === 'true') {
      appInBack = this.props.t("REPORTDETAIL.background");
    }

    const vmCode = crashMap.isVirtualMachine;

    let modelLabel = crashMap.model || '';
    const modelOriginalName = refinedCrashMap.modelOriginalName || '';

    if (CrashUtil.checkIsGameMatrix(appId, crashMap)) {
      modelLabel = `${modelLabel} (先锋云游戏)`;
    }

    const className = isSmallSize ? [style.report_detail, style.reportDetailSmallSize].join(' ') : style.report_detail;

    if (crashDoc && crashDoc.crashMap) {
      exceptionName = crashDoc.crashMap.expName || exceptionName;
    }

    const showExpAddr = PlatformUtil.isMobileOrMobileLike(pid)
      && (exceptionType || '').startsWith('crash')
      && (exceptionName || '').includes('SIGSEGV');

    const showSecondReport = isAndroid && issueExceptionTypeInt === EXCEPTION_TYPE_INT_ANDROID_NATIVE;

    return (
      <div className={className} style={extraStyle}>
        { isCrashArchived && this.renderArchivedCrashHint() }
        { this.renderDashboardFilterOutHint(esMap) }
        { isMobile(pid) && this.renderSdkVersionHint(crashDoc.crashMap?.sdkVersion, pid) }
        { this.renderSecondReportHint(esMap) }
        { this.renderMetricKitHint(crashMap) }
        { this.renderAexitHint(crashMap) }
        <ul>
          {PlatformUtil.isMobileOrMobileLike(pid) && <li>
            <p className={style.label}>{ this.props.t("REPORTDETAIL.异常进程") }</p>
            <p className={style.detail}>
              {crashMap.processName || "-"}
            </p>
          </li>}
          {!isWechatMiniProgram(pid) && <li>
            <p className={style.label}>{ this.props.t("REPORTDETAIL.异常线程") }</p>
            <p className={style.detail}>
              {crashMap.threadName || '-'}
            </p>
          </li>}
          { showExpAddr && <li>
            <p className={style.label}>{ ze('崩溃时访问地址', 'Accessed Address When Crashed') }</p>
            <p className={style.detail}>{ ReportDetailUtil.renderExpAddr(crashMap.expAddr) }</p>
          </li> }
          { (featureTagInfos || []).length > 0 && <li>
            <p className={style.label}>{ ze('特征标签', 'Feature Tag') }</p>
            <p className={style.detail}>{
              featureTagInfos.map((x, i) => <Tag key={i} color='blue'>{ x.name }</Tag>)
            }</p>
          </li> }
        </ul>
        <ul>
          { this.renderUserId(userId) }
          { this.renderDeviceId(deviceId) }
          <li>
            <p className={style.label}>{ this.props.t("REPORTDETAIL.occurTime") }</p>
            <p className={style.detail}>{crashTimeFormatted}</p>
          </li>
          <li>
            <p className={style.label}>{ this.props.t("REPORTDETAIL.postTime") }</p>
            <p className={style.detail}>{uploadTimeFormatted}</p>
          </li>
          { isMobile(pid) && <li>
            <p className={style.label}>{isAndroid ? this.props.t("REPORTDETAIL.apkName") : this.props.t("REPORTDETAIL.bundleId")}</p>
            <p className={style.detail} title={crashMap.bundleId}>{crashMap.bundleId}</p>
          </li> }
          <li>
            <p className={style.label}>{ this.props.t("REPORTDETAIL.version") }</p>
            <p className={style.detail} title={crashMap.productVersion}>{crashMap.productVersion}</p>
          </li>
          {!isWechatMiniProgram(pid) && !isSwitch(pid) && <li>
            <p className={style.label}>{ this.props.t("REPORTDETAIL.useTime") }</p>
            <p className={style.detail}>{crashDoc.launchTime && crashDoc.launchTime !== '-' ? (formatTime(crashDoc.launchTime * 1000)) : '-'/*'无法获取'*/}</p>
          </li>}
          { PlatformUtil.isMobileOrMobileLike(pid) && <li>
            <p className={style.label}>{ this.props.t("REPORTDETAIL.feStatus") }</p>
            <p className={style.detail}>{appInBack || '-'}</p>
          </li>}
          { this.renderStartupHang(crashMap) }
          { (PlatformUtil.isMobileOrMobileLike(pid) || modelLabel) && <li>
            <p className={style.label}>{ this.props.t(isAndroid ? "oomFieldKeys.model" :"REPORTDETAIL.设备商品名") }</p>
            <p className={style.detail}>
              <div>{ BrandIconUtil.makeDeviceNameLabelWithIcon(pid, modelOriginalName, modelLabel) }</div>
            </p>
          </li> }
          {!isSwitch(pid) && <li>
            <p className={style.label}>{ this.props.t("REPORTDETAIL.osVersion") }</p>
            <p className={style.detail}>
              { decodeURIComponent(crashMap.osVer) }
            </p>
          </li>}
          { this.renderCpu(crashMap) }
          { this.renderSoc(crashMap) }
          { this.renderStartupCrash(crashMap) }
          { !EnvUtil.isChinaEnv() && this.renderCountryOrRegion(srcCountry) }
          { this.renderMemory(crashMap, esMap) }
          {
            isAndroid && (
              <li>
                <p className={style.label}>{ this.props.t("REPORTDETAIL.rom") }</p>
                <p className={style.detail}>
                  { decodeURIComponent(rom) }
                </p>
              </li>
            )
          }
          {
            isAndroid && esMap.gpu && (<li>
              <p className={style.label}>GPU</p>
              <p className={style.detail}>{esMap.gpu}</p>
            </li>)
          }
          {
            isAndroid && this.renderVmType(crashMap, esMap)
          }
          {
            isIos && vmType === VmTypeInt.PLAYCOVER && (<li>
              <p className={style.label}>{ ze('基于PlayCover', 'Run on PlayCover') }</p>
              <p className={style.detail}>{ t('common.yes') }</p>
            </li>)
          }
          {
            PlatformUtil.isMobileOrMobileLike(pid) && (<li>
              <p className={style.label}>{ isAndroid ? t("TRACKDATATAB.isRoot") : t("TRACKDATATAB.isJailbreak") }</p>
              <p className={style.detail}>{ String(crashMap.isRooted) === 'true' ? t("TRACKDATATAB.yes") : t("TRACKDATATAB.no") }</p>
            </li>)
          }
          {!isSwitch(pid) && !isLinux(pid) && <li>
            <p className={style.label} style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
              <div>{ t("REPORTDETAIL.expUid") }</div>
              <Tooltip
                title={<div>
                  {ze(
                    '每条上报的唯一标识，由SDK内部生成，对应客户端日志中的expuid。',
                    'The unique identifier of each report, generated internally by the SDK, corresponds to the expuid in the client log.',
                  )}
                  <a
                    href={DocsUtil.makeDocsUrl('/terms/#CommonTerms')}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {ze('查看更多', 'View More')}
                  </a>
                </div>}
              ><WrappedTipsIcon /></Tooltip>
            </p>
            <p className={style.detail}>{ esMap.expUid || '-' }</p>
          </li>}
          {
            isAndroid && isNotNullish(appBit) && (<li>
              <p className={style.label}>{ t('issueCrashFilterKey.appBit') }</p>
              <p className={style.detail}>{appBit > 0 ? `${appBit}${ze('位', ' Bit')}` : ze('未识别', 'Unknown') }</p>
            </li>)
          }
          { hasMinidump && <li>
            <p className={style.label}>{ t("ERRORSTACKTAB.下载原始dump") }</p>
            <Button type='primary' onClick={() => this.onDownloadDump()}>{t('REPORTDETAIL.点击下载')}</Button>
          </li>}
          { PlatformUtil.isMobileOrMobileLike(pid) && <li>
            <p className={style.label} style={{ display: 'flex', alignItems: 'center', gap: '2px'}}>
              <div>{ this.props.t("REPORTDETAIL.是否二次上报")}</div>
              <Tooltip title={SIGNAL_PROCESSING_STATUS_TOOLTIP}>
                <WrappedTipsIcon />
              </Tooltip>
            </p>
            <p className={style.detail}
              style={{color: esMap.isSecondReport ? CS_STYLES.PRIMARY_COLOR_ORANGE : 'inherit'}}
            >{ esMap.isSecondReport ? t('REPORTDETAIL.secondReportTrue') : t('REPORTDETAIL.secondReportFalse') }</p>
          </li> }
          { !!esMap.userSceneTag && <li>
            <p className={style.label}>{ this.getUserSceneTagLabel() }</p>
            <p className={style.detail}>{ esMap.userSceneTag }</p>
          </li> }
          { integratedOriginAppId && <li>
            <p className={style.label}>{ t("REPORTDETAIL.原始转发项目") }</p>
            <p className={style.detail}>
              <div>{ integratedOriginAppName || '-' }</div>
              <div style={{ fontSize: '12px', color: CS_STYLES.PRIMARY_COLOR, lineHeight: 'normal' }}>App ID: { integratedOriginAppId }</div>
            </p>
          </li> }
          { this.renderCloudGamingFields(crashMap, esMap) }
          { this.getIsShowChannelId() && !!esMap.channelId && <li>
            <p className={style.label}>Channel ID</p>
            <p className={style.detail}>{ esMap.channelId }</p>
          </li>  }
          { this.renderCustomKvList(displayedCustomKvList, crashMap) }
          { !this.getIsFromShareLink() && <PsMatchedReportLink
            appId={appId}
            matchUuid={crashMap?.matchUuid}
            uploadTimeFormatted={uploadTimeFormatted}
          /> }
          { !this.getIsFromShareLink() && <UserSecurityStrategy
            appId={appId}
            userId={userId}
            uploadTimeFormatted={uploadTimeFormatted}
          /> }
        </ul>
        { this.renderReportScreenshot(appId, pid, crashMap) }
        { this.renderReportJankSampleFlameGraph() }
      </div>
    );
  }
}

ReportDetail.propTypes = {
  appId: PropTypes.string,
  issue: PropTypes.object,
  pid: PropTypes.string,
  exceptionType: PropTypes.string,
  getHasSpecifiedPermission: PropTypes.func,
  appIdToWebAppSettings: PropTypes.object,
  isSmallSize: PropTypes.bool,  // 是否显示为小尺寸（在issue预览的时候显示用）
  shareId: PropTypes.string,  // 分享链接的id，如果不为空表示是通过分享链接查看的
  shareLinkEquivalentUserPermissions: PropTypes.array, // 分享链接的相关查看权限
  currentUser: PropTypes.object,
  hasSdkScreenshot: PropTypes.bool,  // SDK是否上传了截图
};
